/**
 * Finance Phase 1 (2026-10-28): partners, the historical months and the opening
 * position — their words in English and Arabic, spread into the one dictionary in
 * i18n.tsx so a missing key is still a compile error. Kept in a file of their own
 * so the dictionary's other edits never meet these.
 */

export const booksEn = {
  // Navigation
  'nav.groupBooks': 'Owners & history',
  'nav.partners': 'Partners',
  'nav.partnerTransactions': 'Partner transactions',
  'nav.historical': 'Historical finance',
  'nav.financialSetup': 'Financial setup',

  // Evidence files
  'evidence.choose': 'Choose files',
  'evidence.drop': 'Drop files here, or choose them — {types}',
  'evidence.uploading': 'uploading…',
  'evidence.open': 'Open',
  'evidence.download': 'Download',
  'evidence.removeConfirm': 'Take it off',
  'evidence.none': 'No file yet.',
  'evidence.tooLarge': '{name} is larger than {mb} MB.',
  'evidence.wrongType': '{name} cannot go here — it takes {types}.',
  'evidence.failed': '{name} did not upload: {reason}',

  // Partners
  'partners.title': 'Partners',
  'partners.subtitle': 'The company’s owners: what each owns, and their money with the company.',
  'partners.new': 'New partner',
  'partners.edit': 'Edit partner',
  'partners.name': 'Name',
  'partners.nameAr': 'Name in Arabic',
  'partners.share': 'Ownership',
  'partners.shareToday': 'Share today',
  'partners.shareFrom': 'From',
  'partners.shareTo': 'To',
  'partners.shareNote': 'Note',
  'partners.shareHint': '0–100, up to six decimal places. Ownership moves no money and pays nothing.',
  'partners.shareStarts': 'Holds it from',
  'partners.changeShare': 'Change share',
  'partners.shareHistory': 'Ownership through time',
  'partners.shareForward': 'A new share starts on or after the latest one ({date}); the latest’s own day corrects it.',
  'partners.ownershipTotal': 'Ownership on record',
  'partners.ownershipComplete': 'Comes to 100%.',
  'partners.ownershipOff': 'Does not come to 100%. Treasury or unallocated shares can explain it — check the shares.',
  'partners.status': 'Status',
  'partners.active': 'Active',
  'partners.inactive': 'Inactive',
  'partners.inactiveHint': 'An inactive partner takes only repayments and adjustments.',
  'partners.contact': 'Contact',
  'partners.recordTransaction': 'Record transaction',
  'partners.allTransactions': 'All transactions',
  'partners.loanPayable': 'Loan payable',
  'partners.loanPayableHint': 'The company owes the partner',
  'partners.advanceReceivable': 'Advances receivable',
  'partners.advanceReceivableHint': 'The partner owes the company',
  'partners.informationalNet': 'Net, for information',
  'partners.informationalNetHint': 'Loan less advances. Not an offset: netting them takes an approved settlement.',
  'partners.equity': 'Capital put in',
  'partners.equityHint': 'Not a debt',
  'partners.distributions': 'Profit paid out',
  'partners.loanRepaid': 'Loan repaid',
  'partners.noMoney': 'Nothing recorded yet.',
  'partners.noCutover': 'Until the opening position is confirmed in Financial setup, balances count everything recorded.',
  'partners.sinceCutover': 'Balances are the opening position on {date} plus what was recorded since.',
  'partners.opening': 'Opening position on {date}',
  'partners.statement': 'Statement',
  'partners.balancesAfter': 'Balances after',
  'partners.history': 'History — held in the opening position',
  'partners.reversed': 'Reversed',
  'partners.currencyBalances': 'In {currency}',
  'partners.empty': 'No partners yet.',

  // Partner transactions
  'ptx.title': 'Partner transactions',
  'ptx.new': 'Record a partner transaction',
  'ptx.number': 'Number',
  'ptx.partner': 'Partner',
  'ptx.type': 'What happened',
  'ptx.date': 'Date',
  'ptx.amount': 'Amount',
  'ptx.account': 'Company account',
  'ptx.accountHint': 'Where the money came in or went out. Only accounts in the transaction’s currency.',
  'ptx.noAccounts': 'No account in {currency} yet — add one in Financial setup.',
  'ptx.rate': 'Rate to {base} on the day',
  'ptx.rateHint': 'Saved with the transaction and never recalculated. Today’s rate in Settings › Currencies: {rate}.',
  'ptx.rateNone': 'Saved with the transaction and never recalculated. There is no rate in Settings › Currencies yet.',
  'ptx.baseValue': 'In {base}',
  'ptx.reference': 'Reference',
  'ptx.notes': 'Notes',
  'ptx.reason': 'Why',
  'ptx.evidence': 'Evidence',
  'ptx.expense': 'The expense they paid',
  'ptx.expenseHint': 'Already recorded in Expenses — record it there first if it is not. It is never copied.',
  'ptx.expenseSearch': 'Search expenses',
  'ptx.expenseRemaining': '{amount} not yet paid by a partner',
  'ptx.noExpenses': 'No expense in {currency} is left for a partner to have paid.',
  'ptx.funding': 'The money becomes',
  'ptx.fundingLOAN': 'A loan the company owes them',
  'ptx.fundingCAPITAL': 'Capital they put in',
  'ptx.balance': 'Balance',
  'ptx.direction': 'Direction',
  'ptx.INCREASE': 'Raise it',
  'ptx.DECREASE': 'Lower it',
  'ptx.adjustedLOAN_PAYABLE': 'Loan payable',
  'ptx.adjustedADVANCE_RECEIVABLE': 'Advances receivable',
  'ptx.adjustedEQUITY': 'Capital put in',
  'ptx.moneyIn': 'Money in',
  'ptx.moneyOut': 'Money out',
  'ptx.noMoney': 'No company money',
  'ptx.groupIn': 'Money comes into the company',
  'ptx.groupOut': 'Money goes out of the company',
  'ptx.groupNone': 'No company money moves',
  'ptx.nowOwed': 'Now: the company owes {loan}; the partner owes {advance}.',
  'ptx.beforeCutover': 'This is before the cutover ({date}): it is kept as history and moves no balance — the opening position holds it.',
  'ptx.saved': 'Recorded.',
  'ptx.status': 'Status',
  'ptx.posted': 'Posted',
  'ptx.reverse': 'Reverse',
  'ptx.reverseTitle': 'Reverse {number}',
  'ptx.reverseHint': 'The transaction stays on record and counts nowhere. Undoing it may not leave a balance below nothing.',
  'ptx.reversedOn': 'Reversed on {date}: {reason}',
  'ptx.journal': 'What it stands for in double entry',
  'ptx.journalHint': 'Shown for the accounting meaning; nothing is posted to a ledger.',
  'ptx.debit': 'Debit',
  'ptx.credit': 'Credit',
  'ptx.history': 'History',
  'ptx.counted': 'Counts in today’s balances',
  'ptx.notCounted': 'Counts nowhere',
  'ptx.CAPITAL_CONTRIBUTION': 'Capital put in',
  'ptx.CAPITAL_CONTRIBUTION.hint': 'The partner puts money in as capital — not a loan, not revenue.',
  'ptx.LOAN_RECEIVED': 'Partner lent money to the company',
  'ptx.LOAN_RECEIVED.hint': 'The company owes it back — not revenue.',
  'ptx.LOAN_REPAYMENT': 'Company repaid the partner’s loan',
  'ptx.LOAN_REPAYMENT.hint': 'Lowers what the company owes them — not an expense.',
  'ptx.ADVANCE_PAID': 'Repayable advance to the partner',
  'ptx.ADVANCE_PAID.hint': 'The partner owes it back — not salary, not an expense.',
  'ptx.ADVANCE_REPAYMENT': 'Partner returned an advance',
  'ptx.ADVANCE_REPAYMENT.hint': 'Lowers what the partner owes — not revenue.',
  'ptx.PROFIT_DISTRIBUTION': 'Profit paid out to the partner',
  'ptx.PROFIT_DISTRIBUTION.hint': 'An approved distribution — not an expense, not a loan repayment.',
  'ptx.PAID_COMPANY_EXPENSE': 'Partner paid a company expense personally',
  'ptx.PAID_COMPANY_EXPENSE.hint': 'No company money moved; the expense is linked, never recorded twice.',
  'ptx.ADJUSTMENT': 'Correction to a balance',
  'ptx.ADJUSTMENT.hint': 'One balance, one direction, with a reason. The Finance admin’s.',
  'ptx.account.CASH': 'Cash',
  'ptx.account.PARTNER_LOAN_PAYABLE': 'Partner loan payable',
  'ptx.account.PARTNER_ADVANCE_RECEIVABLE': 'Partner advances receivable',
  'ptx.account.PARTNER_EQUITY': 'Partner capital',
  'ptx.account.PARTNER_DISTRIBUTIONS': 'Distributions',
  'ptx.account.EXPENSE': 'Company expense',
  'ptx.account.OWNER_ADJUSTMENTS': 'Owner adjustments',
  'ptx.error.PARTNER_BALANCE_EXCEEDED': 'That is more than the balance holds.',
  'ptx.error.IDEMPOTENCY_CONFLICT': 'This form was already used for another transaction. Open it again.',

  // Historical finance
  'historical.title': 'Historical finance',
  'historical.subtitle': 'One total a month of salaries or other expenses paid before the system held them, with the file it came from.',
  'historical.summary': 'Monthly summary',
  'historical.salaries': 'Historical salaries',
  'historical.expenses': 'Historical expenses',
  'historical.HISTORICAL_SALARIES': 'Salaries',
  'historical.HISTORICAL_EXPENSES': 'Other expenses',
  'historical.new': 'Enter a month',
  'historical.newSalaries': 'Enter a month of salaries',
  'historical.newExpenses': 'Enter a month of expenses',
  'historical.edit': 'Edit the month',
  'historical.type': 'Type',
  'historical.month': 'Month',
  'historical.monthHint': 'The month it was paid in. Any month that has begun.',
  'historical.total': 'Total paid',
  'historical.rate': 'Rate to {base} that month',
  'historical.rateHint': 'The rate that applied then, typed by you — needed to finalize. Today’s ({rate}) is shown only for reference.',
  'historical.rateHintNone': 'The rate that applied then, typed by you — needed to finalize.',
  'historical.base': 'In {base}',
  'historical.source': 'Source',
  'historical.sourceHint': 'Where the figure came from — “Legacy payroll workbook”.',
  'historical.reference': 'Reference there',
  'historical.files': 'Excel or PDF files',
  'historical.filesHint': 'The legacy file is evidence only: nothing is read from it, and changing files never changes the amount.',
  'historical.paid': 'Paid in the past',
  'historical.paidHint': 'A past cost for the P&L and trends — never a payable, never today’s money.',
  'historical.missingDocument': 'Missing supporting document',
  'historical.documents': 'Files',
  'historical.withDocuments': 'With files',
  'historical.withoutDocuments': 'Missing files',
  'historical.salariesCol': 'Salaries',
  'historical.expensesCol': 'Other expenses',
  'historical.totalCol': 'Historical costs',
  'historical.inBase': 'in {base}',
  'historical.notCovered': 'No record — unknown, not zero',
  'historical.draftNotCounted': 'Draft — not counted',
  'historical.unconverted': 'Not valued in {base}',
  'historical.monthsMissing': '{salaries} months without salaries, {expenses} without expenses',
  'historical.range': 'Months',
  'historical.finalize': 'Finalize',
  'historical.finalizeTitle': 'Finalize {month}',
  'historical.finalizeHint': 'Once finalized it counts in the historical totals, and changes only by a correction with its reason.',
  'historical.overlap': 'Already recorded for this month',
  'historical.overlapNone': 'Nothing in payroll or expenses for this month, and no other record of it.',
  'historical.overlapIntro': 'Nothing is subtracted for you. Check these, then confirm the total leaves them out.',
  'historical.finding.PAYROLL_PERIOD': 'Payroll period {status}: {amount}, {count} payslips',
  'historical.finding.SALARY_EXPENSES': 'Salary expenses: {amount} in {count}',
  'historical.finding.EXPENSES': 'Expenses: {amount} in {count}',
  'historical.finding.OTHER_CURRENCY_RECORD': 'This month in {currency} too: {amount} ({status})',
  'historical.finding.AFTER_CUTOVER': 'The month reaches past the cutover ({date}), where the live modules count — the Finance admin reviews it.',
  'historical.decision': 'My total leaves out what is already recorded above',
  'historical.decisionNote': 'What you checked',
  'historical.decisionNoteHint': 'Kept with the month: which records you looked at, and why the total does not count them twice.',
  'historical.correct': 'Correct',
  'historical.correctTitle': 'Correct {month}',
  'historical.correctHint': 'The month keeps a revision of what it was. It is reviewed again against payroll, expenses and the cutover as they are now.',
  'historical.void': 'Void',
  'historical.voidTitle': 'Void {month}',
  'historical.voidHint': 'It stays on record and counts nowhere; a new record can take its place.',
  'historical.revisions': 'Revisions',
  'historical.revision.FINALIZED': 'Finalized',
  'historical.revision.CORRECTED': 'Corrected',
  'historical.revision.VOIDED': 'Voided',
  'historical.removedFiles': 'Files taken off',
  'historical.status.DRAFT': 'Draft',
  'historical.status.FINALIZED': 'Finalized',
  'historical.status.VOIDED': 'Voided',
  'historical.review': 'Review',
  'historical.reviewedOn': 'Reviewed on {date}',
  'historical.error.DUPLICATE_RESOURCE': 'That month already has a record of this kind in this currency — add the file to it instead.',
  'historical.error.HISTORICAL_OVERLAP_UNRESOLVED': 'Payroll or expenses already hold something for this month: review it below.',

  // Financial setup
  'setup.title': 'Financial setup',
  'setup.subtitle': 'The day the books move onto the system, what each account held that morning, and what partners were owed and owed.',
  'setup.cutover': 'Accounting cutover',
  'setup.cutoverDate': 'Cutover day',
  'setup.cutoverHint': 'From this day, balances are the opening position plus what is recorded. Anything earlier is history.',
  'setup.noCutover': 'No cutover yet. Set the day the books move onto the system.',
  'setup.setCutover': 'Set the cutover',
  'setup.changeCutover': 'Change the day',
  'setup.baseCurrency': 'Base currency',
  'setup.baseCurrencyHint': 'Settings › Finance. Opening balances are valued in it.',
  'setup.status.DRAFT': 'Draft',
  'setup.status.CONFIRMED': 'Confirmed',
  'setup.confirmedBy': 'Confirmed on {date}',
  'setup.reopenedBy': 'Reopened on {date}: {reason}',
  'setup.confirm': 'Confirm the opening position',
  'setup.confirmHint': 'Once confirmed it is fixed: reopening it takes the Finance admin and a reason. What the reconciliation says now is kept with it.',
  'setup.reopen': 'Reopen',
  'setup.reopenTitle': 'Reopen the opening position',
  'setup.reopenHint': 'It goes back to a draft. Partner balances count every recorded transaction again until it is confirmed; the confirmed figures stay in the audit trail.',
  'setup.accounts': 'Cash and bank accounts',
  'setup.newAccount': 'New account',
  'setup.editAccount': 'Edit account',
  'setup.accountName': 'Name',
  'setup.accountType': 'Type',
  'setup.accountCASH': 'Cash',
  'setup.accountBANK': 'Bank',
  'setup.bankName': 'Bank',
  'setup.reference': 'How it is known',
  'setup.referenceHint': '“•••• 4821”, “Office safe” — never a full account number.',
  'setup.accountCurrencyLocked': 'It holds money already, so its currency stays.',
  'setup.accountClosed': 'Closed',
  'setup.accountOpen': 'Open',
  'setup.closeAccount': 'Closed — takes nothing new',
  'setup.noAccounts': 'No accounts yet.',
  'setup.opening': 'Opening balances',
  'setup.openingCash': 'Cash and bank',
  'setup.openingPartners': 'Partners',
  'setup.addLine': 'Add a balance',
  'setup.editLine': 'Change a balance',
  'setup.lineKind': 'Balance',
  'setup.kind.CASH': 'What an account held',
  'setup.kind.PARTNER_LOAN_PAYABLE': 'Loan the company owed the partner',
  'setup.kind.PARTNER_ADVANCE_RECEIVABLE': 'Advances the partner owed',
  'setup.kind.PARTNER_EQUITY': 'Capital the partner had put in',
  'setup.lineRate': 'Rate to {base} on the cutover day',
  'setup.lineRateHint': 'Typed by you: the rate that applied on the cutover day.',
  'setup.lineOldBase': 'Valued in {currency}: save it again',
  'setup.noLines': 'No opening balances yet.',
  'setup.removeLine': 'Remove',
  'setup.removeLineConfirm': 'Remove this balance',
  'setup.files': 'Evidence',
  'setup.filesHint': 'Bank statements, a partner’s confirmation — PDF, Excel or a picture.',
  'setup.reconciliation': 'Reconciliation',
  'setup.reconciliationHint': 'Opening balance + what was recorded since = what each account should hold today.',
  'setup.openingCol': 'Opening',
  'setup.inCol': 'In',
  'setup.outCol': 'Out',
  'setup.expectedCol': 'Should hold',
  'setup.movementsCol': 'Movements',
  'setup.noOpening': 'none — opens at 0',
  'setup.unassigned': 'Not assigned to an account',
  'setup.unassignedHint': 'Payments and expenses are recorded without an account; they count in the currency totals only.',
  'setup.source.PAYMENTS': 'Customer payments',
  'setup.source.EXPENSES': 'Paid expenses',
  'setup.source.POINTS_CASH': 'Points cash paid by hand',
  'setup.source.PARTNERS': 'Partner money',
  'setup.byCurrency': 'By currency',
  'setup.partnersPreview': 'Partner balances',
  'setup.issues': 'Before confirming',
  'setup.issuesNone': 'Nothing stands in the way.',
  'setup.blocking': 'Blocks confirming',
  'setup.warning': 'Worth a look',
  'setup.issue.CUTOVER_IN_FUTURE': 'The cutover day has not come yet.',
  'setup.issue.LINE_IN_OLD_BASE': 'A balance was valued in {valuedIn}; the base currency is now {baseCurrency}. Save it again.',
  'setup.issue.LINE_CURRENCY_MISMATCH': 'A balance of {label} is not in its account’s currency.',
  'setup.issue.HISTORY_AFTER_CUTOVER': 'A finalized historical month ({month}) reaches past the cutover and was not reviewed against it. Correct it, with the review, or move the cutover.',
  'setup.issue.DRAFT_HISTORY_AFTER_CUTOVER': 'A draft historical month ({month}) reaches past the cutover: it will need the review.',
  'setup.issue.ACCOUNT_WITHOUT_OPENING': '{label} has no opening balance: it opens at 0.',
  'setup.issue.OPENING_ON_INACTIVE_ACCOUNT': '{label} is closed but has an opening balance.',
  'setup.issue.PARTNER_HISTORY_WITHOUT_OPENING': '{label} has money recorded before the cutover but no opening position.',
  'setup.issue.PARTNER_EXPENSE_CANCELLED': 'A partner paid “{label}”, which has since been cancelled — their balance still holds it.',
  'setup.issue.UNASSIGNED_MOVEMENTS': '{count} payments and expenses since the cutover name no account; they count per currency.',
  'setup.issue.NEGATIVE_EXPECTED_BALANCE': '{label} should hold less than nothing ({expected}).',
  'setup.policy': 'Partner advances',
  'setup.advanceLimit': 'Most a partner may owe in advances',
  'setup.advanceLimitHint': 'In {base}, at today’s rates. A new advance past it is refused. 0: no limit.',
  'setup.saved': 'Saved.',
  'setup.error.CUTOVER_CONFIRMED': 'The opening position is confirmed. Reopen it first.',
  'setup.error.CUTOVER_NOT_READY': 'Something blocks confirming — see “Before confirming”.',
  'setup.error.CONCURRENT_CHANGE': 'Someone changed the opening position meanwhile. Look at it again.',
} as const;

export type BooksKey = keyof typeof booksEn;

export const booksAr: Record<BooksKey, string> = {
  'nav.groupBooks': 'الملاك والسجل التاريخي',
  'nav.partners': 'الشركاء',
  'nav.partnerTransactions': 'معاملات الشركاء',
  'nav.historical': 'البيانات المالية التاريخية',
  'nav.financialSetup': 'الإعداد المالي',

  'evidence.choose': 'اختر ملفات',
  'evidence.drop': 'اسحب الملفات إلى هنا أو اخترها — {types}',
  'evidence.uploading': 'جاري الرفع…',
  'evidence.open': 'فتح',
  'evidence.download': 'تنزيل',
  'evidence.removeConfirm': 'إزالة الملف',
  'evidence.none': 'لا يوجد ملف بعد.',
  'evidence.tooLarge': '{name} أكبر من {mb} ميجابايت.',
  'evidence.wrongType': 'لا يمكن رفع {name} هنا. الأنواع المقبولة: {types}.',
  'evidence.failed': 'فشل رفع {name}: {reason}',

  'partners.title': 'الشركاء',
  'partners.subtitle': 'ملاك الشركة: حصة كل منهم، وأموالهم لدى الشركة.',
  'partners.new': 'شريك جديد',
  'partners.edit': 'تعديل الشريك',
  'partners.name': 'الاسم',
  'partners.nameAr': 'الاسم بالعربية',
  'partners.share': 'نسبة الملكية',
  'partners.shareToday': 'النسبة اليوم',
  'partners.shareFrom': 'من',
  'partners.shareTo': 'إلى',
  'partners.shareNote': 'ملاحظة',
  'partners.shareHint': 'من 0 إلى 100، وحتى ست خانات عشرية. الملكية لا تحرّك مالًا ولا تدفع شيئًا.',
  'partners.shareStarts': 'يملكها اعتبارًا من',
  'partners.changeShare': 'تغيير النسبة',
  'partners.shareHistory': 'سجل الملكية',
  'partners.shareForward': 'تبدأ النسبة الجديدة من تاريخ آخر نسبة ({date}) أو بعده. وإذا بدأت في اليوم نفسه، فهي تصحّح آخر نسبة.',
  'partners.ownershipTotal': 'مجموع الملكية المسجّلة',
  'partners.ownershipComplete': 'يبلغ 100%.',
  'partners.ownershipOff': 'لا يبلغ 100%. قد يرجع ذلك إلى أسهم خزينة أو أسهم غير موزعة، فراجع النسب.',
  'partners.status': 'الحالة',
  'partners.active': 'نشط',
  'partners.inactive': 'غير نشط',
  'partners.inactiveHint': 'للشريك غير النشط، يمكنك تسجيل السداد والتصحيحات فقط.',
  'partners.contact': 'بيانات التواصل',
  'partners.recordTransaction': 'تسجيل معاملة',
  'partners.allTransactions': 'كل المعاملات',
  'partners.loanPayable': 'قرض مستحق للشريك',
  'partners.loanPayableHint': 'ما تدين به الشركة للشريك',
  'partners.advanceReceivable': 'سلف مستحقة على الشريك',
  'partners.advanceReceivableHint': 'ما يدين به الشريك للشركة',
  'partners.informationalNet': 'الصافي، للعلم فقط',
  'partners.informationalNetHint': 'القرض ناقص السلف. وهذا ليس مقاصّة، فالمقاصّة تحتاج إلى تسوية معتمدة.',
  'partners.equity': 'رأس المال المدفوع',
  'partners.equityHint': 'ليس دينًا',
  'partners.distributions': 'أرباح موزعة',
  'partners.loanRepaid': 'المسدد من القرض',
  'partners.noMoney': 'لا يوجد شيء مسجل بعد.',
  'partners.noCutover': 'حتى يتم اعتماد المركز الافتتاحي في «الإعداد المالي»، تشمل الأرصدة كل ما تم تسجيله.',
  'partners.sinceCutover': 'الأرصدة هي المركز الافتتاحي في {date}، مضافًا إليه ما تم تسجيله بعد ذلك.',
  'partners.opening': 'المركز الافتتاحي في {date}',
  'partners.statement': 'كشف الحساب',
  'partners.balancesAfter': 'الأرصدة بعدها',
  'partners.history': 'سجل تاريخي — ضمن المركز الافتتاحي',
  'partners.reversed': 'معكوسة',
  'partners.currencyBalances': 'بعملة {currency}',
  'partners.empty': 'لا يوجد شركاء بعد.',

  'ptx.title': 'معاملات الشركاء',
  'ptx.new': 'تسجيل معاملة لشريك',
  'ptx.number': 'الرقم',
  'ptx.partner': 'الشريك',
  'ptx.type': 'ما الذي حدث',
  'ptx.date': 'التاريخ',
  'ptx.amount': 'المبلغ',
  'ptx.account': 'حساب الشركة',
  'ptx.accountHint': 'الحساب الذي دخلت إليه الأموال أو خرجت منه. تظهر هنا الحسابات بعملة المعاملة فقط.',
  'ptx.noAccounts': 'لا يوجد حساب بعملة {currency} بعد. أضف حسابًا من «الإعداد المالي».',
  'ptx.rate': 'سعر الصرف مقابل {base} في ذلك اليوم',
  'ptx.rateHint': 'نحفظه مع المعاملة ولا نعيد حسابه أبدًا. سعر اليوم في «الإعدادات › العملات»: {rate}.',
  'ptx.rateNone': 'نحفظه مع المعاملة ولا نعيد حسابه أبدًا. لا يوجد سعر في «الإعدادات › العملات» بعد.',
  'ptx.baseValue': 'بعملة {base}',
  'ptx.reference': 'المرجع',
  'ptx.notes': 'ملاحظات',
  'ptx.reason': 'السبب',
  'ptx.evidence': 'المستند',
  'ptx.expense': 'المصروف الذي دفعه',
  'ptx.expenseHint': 'المصروف مسجّل في «المصروفات»، وإن لم يكن فسجّله هناك أولًا. لا يتم نسخه أبدًا.',
  'ptx.expenseSearch': 'ابحث في المصروفات',
  'ptx.expenseRemaining': '{amount} لم يدفعه شريك بعد',
  'ptx.noExpenses': 'لا يوجد مصروف بعملة {currency} لم يدفعه شريك بعد.',
  'ptx.funding': 'يصبح المبلغ',
  'ptx.fundingLOAN': 'قرضًا تدين به الشركة له',
  'ptx.fundingCAPITAL': 'رأس مال دفعه',
  'ptx.balance': 'الرصيد',
  'ptx.direction': 'الاتجاه',
  'ptx.INCREASE': 'زيادة الرصيد',
  'ptx.DECREASE': 'تخفيض الرصيد',
  'ptx.adjustedLOAN_PAYABLE': 'القرض المستحق للشريك',
  'ptx.adjustedADVANCE_RECEIVABLE': 'السلف المستحقة على الشريك',
  'ptx.adjustedEQUITY': 'رأس المال المدفوع',
  'ptx.moneyIn': 'دخول أموال',
  'ptx.moneyOut': 'خروج أموال',
  'ptx.noMoney': 'لا حركة أموال',
  'ptx.groupIn': 'أموال تدخل الشركة',
  'ptx.groupOut': 'أموال تخرج من الشركة',
  'ptx.groupNone': 'لا تتحرك أموال الشركة',
  'ptx.nowOwed': 'الآن: تدين الشركة بـ {loan}، ويدين الشريك بـ {advance}.',
  'ptx.beforeCutover': 'هذا قبل تاريخ الانتقال ({date})، فنحفظ المعاملة سجلًا تاريخيًا ولا تغيّر أي رصيد، لأن المركز الافتتاحي يشملها.',
  'ptx.saved': 'تم التسجيل.',
  'ptx.status': 'الحالة',
  'ptx.posted': 'مرحّلة',
  'ptx.reverse': 'عكس',
  'ptx.reverseTitle': 'عكس المعاملة {number}',
  'ptx.reverseHint': 'تبقى المعاملة في السجل، لكنها لا تدخل في أي رصيد. ولا يمكن عكسها إذا كان ذلك سيجعل أي رصيد أقل من الصفر.',
  'ptx.reversedOn': 'تم عكسها في {date}: {reason}',
  'ptx.journal': 'ما تمثّله في القيد المزدوج',
  'ptx.journalHint': 'للتوضيح المحاسبي فقط، ولا يتم ترحيل أي شيء إلى دفتر الأستاذ.',
  'ptx.debit': 'مدين',
  'ptx.credit': 'دائن',
  'ptx.history': 'سجل تاريخي',
  'ptx.counted': 'تدخل في أرصدة اليوم',
  'ptx.notCounted': 'لا تدخل في أي رصيد',
  'ptx.CAPITAL_CONTRIBUTION': 'مساهمة في رأس المال',
  'ptx.CAPITAL_CONTRIBUTION.hint': 'يدفع الشريك أموالًا كرأس مال، وهي ليست قرضًا ولا إيرادًا.',
  'ptx.LOAN_RECEIVED': 'أقرض الشريك الشركة',
  'ptx.LOAN_RECEIVED.hint': 'على الشركة أن تردّه، وهو ليس إيرادًا.',
  'ptx.LOAN_REPAYMENT': 'سدّدت الشركة قرض الشريك',
  'ptx.LOAN_REPAYMENT.hint': 'يخفّض ما تدين به الشركة للشريك، وهو ليس مصروفًا.',
  'ptx.ADVANCE_PAID': 'سلفة للشريك واجبة الردّ',
  'ptx.ADVANCE_PAID.hint': 'على الشريك أن يردّها، وهي ليست راتبًا ولا مصروفًا.',
  'ptx.ADVANCE_REPAYMENT': 'ردّ الشريك سلفة',
  'ptx.ADVANCE_REPAYMENT.hint': 'يخفّض ما يدين به الشريك للشركة، وهو ليس إيرادًا.',
  'ptx.PROFIT_DISTRIBUTION': 'توزيع أرباح على الشريك',
  'ptx.PROFIT_DISTRIBUTION.hint': 'توزيع أرباح معتمد، وهو ليس مصروفًا ولا سدادًا لقرض.',
  'ptx.PAID_COMPANY_EXPENSE': 'دفع الشريك مصروفًا للشركة من ماله',
  'ptx.PAID_COMPANY_EXPENSE.hint': 'لم تتحرك أموال الشركة. نربط المصروف بالمعاملة ولا نسجّله مرتين.',
  'ptx.ADJUSTMENT': 'تصحيح رصيد',
  'ptx.ADJUSTMENT.hint': 'رصيد واحد في اتجاه واحد، مع ذكر السبب. من صلاحية مدير المالية فقط.',
  'ptx.account.CASH': 'النقدية',
  'ptx.account.PARTNER_LOAN_PAYABLE': 'قروض الشركاء المستحقة',
  'ptx.account.PARTNER_ADVANCE_RECEIVABLE': 'سلف الشركاء المستحقة',
  'ptx.account.PARTNER_EQUITY': 'رأس مال الشركاء',
  'ptx.account.PARTNER_DISTRIBUTIONS': 'الأرباح الموزعة',
  'ptx.account.EXPENSE': 'مصروف الشركة',
  'ptx.account.OWNER_ADJUSTMENTS': 'تسويات الملاك',
  'ptx.error.PARTNER_BALANCE_EXCEEDED': 'المبلغ أكبر من الرصيد.',
  'ptx.error.IDEMPOTENCY_CONFLICT': 'تم استخدام هذا النموذج لمعاملة أخرى. افتحه من جديد.',

  'historical.title': 'البيانات المالية التاريخية',
  'historical.subtitle': 'إجمالي واحد لكل شهر للرواتب أو المصروفات الأخرى التي دفعتها الشركة قبل أن يبدأ النظام في تسجيلها، مع الملف المصدر.',
  'historical.summary': 'الملخص الشهري',
  'historical.salaries': 'الرواتب التاريخية',
  'historical.expenses': 'المصروفات التاريخية',
  'historical.HISTORICAL_SALARIES': 'الرواتب',
  'historical.HISTORICAL_EXPENSES': 'مصروفات أخرى',
  'historical.new': 'إدخال شهر',
  'historical.newSalaries': 'إدخال شهر رواتب',
  'historical.newExpenses': 'إدخال شهر مصروفات',
  'historical.edit': 'تعديل الشهر',
  'historical.type': 'النوع',
  'historical.month': 'الشهر',
  'historical.monthHint': 'الشهر الذي تم الدفع فيه. يمكنك اختيار أي شهر بدأ بالفعل.',
  'historical.total': 'إجمالي المدفوع',
  'historical.rate': 'سعر الصرف مقابل {base} في ذلك الشهر',
  'historical.rateHint': 'اكتب السعر الذي كان ساريًا وقتها، فهو مطلوب للاعتماد. سعر اليوم ({rate}) للاطلاع فقط.',
  'historical.rateHintNone': 'اكتب السعر الذي كان ساريًا وقتها، فهو مطلوب للاعتماد.',
  'historical.base': 'بعملة {base}',
  'historical.source': 'المصدر',
  'historical.sourceHint': 'مصدر الرقم، مثل «ملف الرواتب القديم».',
  'historical.reference': 'المرجع في المصدر',
  'historical.files': 'ملفات Excel أو PDF',
  'historical.filesHint': 'الملف القديم للإثبات فقط: لا يقرأ النظام منه أي شيء، وتغيير الملفات لا يغيّر المبلغ أبدًا.',
  'historical.paid': 'مدفوع سابقًا',
  'historical.paidHint': 'تكلفة سابقة تظهر في قائمة الدخل والاتجاهات، وليست مبلغًا مستحق الدفع ولا من أموال اليوم.',
  'historical.missingDocument': 'لا يوجد مستند داعم',
  'historical.documents': 'الملفات',
  'historical.withDocuments': 'لها ملفات',
  'historical.withoutDocuments': 'بلا ملفات',
  'historical.salariesCol': 'الرواتب',
  'historical.expensesCol': 'مصروفات أخرى',
  'historical.totalCol': 'التكاليف التاريخية',
  'historical.inBase': 'بعملة {base}',
  'historical.notCovered': 'لا سجل: غير معروف، وليس صفرًا',
  'historical.draftNotCounted': 'مسودة، غير محسوبة',
  'historical.unconverted': 'غير مقيّم بعملة {base}',
  'historical.monthsMissing': 'أشهر بلا رواتب: {salaries}، وبلا مصروفات: {expenses}',
  'historical.range': 'الأشهر',
  'historical.finalize': 'اعتماد',
  'historical.finalizeTitle': 'اعتماد {month}',
  'historical.finalizeHint': 'بعد الاعتماد يدخل الشهر في الإجماليات التاريخية، ولا يتغير إلا بتصحيح مع ذكر السبب.',
  'historical.overlap': 'مسجّل مسبقًا لهذا الشهر',
  'historical.overlapNone': 'لا شيء في الرواتب أو المصروفات لهذا الشهر، ولا سجل آخر له.',
  'historical.overlapIntro': 'لن نطرح شيئًا تلقائيًا. راجع ما يلي، ثم أكّد أن الإجمالي الذي أدخلته لا يشمله.',
  'historical.finding.PAYROLL_PERIOD': 'كشف رواتب ({status}): {amount}، وعدد القسائم {count}',
  'historical.finding.SALARY_EXPENSES': 'مصروفات رواتب: {amount}، وعددها {count}',
  'historical.finding.EXPENSES': 'مصروفات: {amount}، وعددها {count}',
  'historical.finding.OTHER_CURRENCY_RECORD': 'لهذا الشهر سجل بعملة {currency} أيضًا: {amount} ({status})',
  'historical.finding.AFTER_CUTOVER': 'يمتد الشهر إلى ما بعد تاريخ الانتقال ({date})، وبعده نعتمد على سجلات النظام نفسها. يراجعه مدير المالية.',
  'historical.decision': 'الإجمالي الذي أدخلته لا يشمل ما هو مسجّل أعلاه',
  'historical.decisionNote': 'ما الذي راجعته',
  'historical.decisionNoteHint': 'نحفظه مع الشهر: السجلات التي راجعتها، ولماذا لا يحسبها الإجمالي مرتين.',
  'historical.correct': 'تصحيح',
  'historical.correctTitle': 'تصحيح {month}',
  'historical.correctHint': 'يحتفظ الشهر بنسخة مما كان عليه. وستتم مراجعته من جديد مقابل الرواتب والمصروفات وتاريخ الانتقال كما هي الآن.',
  'historical.void': 'إلغاء',
  'historical.voidTitle': 'إلغاء {month}',
  'historical.voidHint': 'يبقى في السجل لكنه لا يدخل في أي إجمالي، ويمكنك إدخال سجل جديد مكانه.',
  'historical.revisions': 'سجل التغييرات',
  'historical.revision.FINALIZED': 'تم الاعتماد',
  'historical.revision.CORRECTED': 'تم التصحيح',
  'historical.revision.VOIDED': 'تم الإلغاء',
  'historical.removedFiles': 'ملفات تمت إزالتها',
  'historical.status.DRAFT': 'مسودة',
  'historical.status.FINALIZED': 'معتمد',
  'historical.status.VOIDED': 'ملغى',
  'historical.review': 'المراجعة',
  'historical.reviewedOn': 'تمت المراجعة في {date}',
  'historical.error.DUPLICATE_RESOURCE': 'لهذا الشهر سجل من هذا النوع وبهذه العملة بالفعل. أضف الملف إلى ذلك السجل بدلًا من إنشاء سجل جديد.',
  'historical.error.HISTORICAL_OVERLAP_UNRESOLVED': 'يوجد في الرواتب أو المصروفات ما يخص هذا الشهر بالفعل، فراجعه أدناه.',

  'setup.title': 'الإعداد المالي',
  'setup.subtitle': 'اليوم الذي تنتقل فيه الدفاتر إلى النظام، وما كان في كل حساب صباح ذلك اليوم، وما للشركاء وما عليهم.',
  'setup.cutover': 'تاريخ الانتقال المحاسبي',
  'setup.cutoverDate': 'يوم الانتقال',
  'setup.cutoverHint': 'من هذا اليوم، تكون الأرصدة هي المركز الافتتاحي مضافًا إليه ما يتم تسجيله. وكل ما قبله سجل تاريخي.',
  'setup.noCutover': 'لا يوجد تاريخ انتقال بعد. حدّد اليوم الذي تنتقل فيه الدفاتر إلى النظام.',
  'setup.setCutover': 'تحديد تاريخ الانتقال',
  'setup.changeCutover': 'تغيير اليوم',
  'setup.baseCurrency': 'العملة الأساسية',
  'setup.baseCurrencyHint': 'تجدها في «الإعدادات › المالية»، وبها نقيّم الأرصدة الافتتاحية.',
  'setup.status.DRAFT': 'مسودة',
  'setup.status.CONFIRMED': 'معتمد',
  'setup.confirmedBy': 'تم الاعتماد في {date}',
  'setup.reopenedBy': 'تمت إعادة فتحه في {date}: {reason}',
  'setup.confirm': 'اعتماد المركز الافتتاحي',
  'setup.confirmHint': 'بعد الاعتماد يصبح ثابتًا، ولا يعيد فتحه إلا مدير المالية مع ذكر السبب. ونحفظ معه نتيجة المطابقة كما هي الآن.',
  'setup.reopen': 'إعادة فتح',
  'setup.reopenTitle': 'إعادة فتح المركز الافتتاحي',
  'setup.reopenHint': 'سيعود مسودة. وحتى يتم اعتماده من جديد، تشمل أرصدة الشركاء كل المعاملات المسجّلة. وتبقى الأرقام المعتمدة في سجل التدقيق.',
  'setup.accounts': 'الحسابات النقدية والبنكية',
  'setup.newAccount': 'حساب جديد',
  'setup.editAccount': 'تعديل الحساب',
  'setup.accountName': 'الاسم',
  'setup.accountType': 'النوع',
  'setup.accountCASH': 'نقدي',
  'setup.accountBANK': 'بنكي',
  'setup.bankName': 'البنك',
  'setup.reference': 'المعرّف',
  'setup.referenceHint': 'مثل «•••• 4821» أو «خزينة المكتب». لا تكتب رقم الحساب كاملًا أبدًا.',
  'setup.accountCurrencyLocked': 'في الحساب أموال بالفعل، فلا يمكن تغيير عملته.',
  'setup.accountClosed': 'مغلق',
  'setup.accountOpen': 'مفتوح',
  'setup.closeAccount': 'مغلق، لا يقبل حركات جديدة',
  'setup.noAccounts': 'لا توجد حسابات بعد.',
  'setup.opening': 'الأرصدة الافتتاحية',
  'setup.openingCash': 'النقدية والبنوك',
  'setup.openingPartners': 'الشركاء',
  'setup.addLine': 'إضافة رصيد',
  'setup.editLine': 'تعديل رصيد',
  'setup.lineKind': 'الرصيد',
  'setup.kind.CASH': 'ما كان في الحساب',
  'setup.kind.PARTNER_LOAN_PAYABLE': 'قرض كانت الشركة تدين به للشريك',
  'setup.kind.PARTNER_ADVANCE_RECEIVABLE': 'سلف كان الشريك يدين بها',
  'setup.kind.PARTNER_EQUITY': 'رأس مال كان الشريك قد دفعه',
  'setup.lineRate': 'سعر الصرف مقابل {base} يوم الانتقال',
  'setup.lineRateHint': 'اكتب السعر الذي كان ساريًا يوم الانتقال.',
  'setup.lineOldBase': 'مقيّم بعملة {currency}، احفظه من جديد',
  'setup.noLines': 'لا توجد أرصدة افتتاحية بعد.',
  'setup.removeLine': 'إزالة',
  'setup.removeLineConfirm': 'إزالة هذا الرصيد',
  'setup.files': 'المستندات',
  'setup.filesHint': 'كشوف حسابات البنك، أو تأكيد من الشريك، بصيغة PDF أو Excel أو صورة.',
  'setup.reconciliation': 'المطابقة',
  'setup.reconciliationHint': 'الرصيد الافتتاحي + ما تم تسجيله بعده = ما يجب أن يكون في كل حساب اليوم.',
  'setup.openingCol': 'الافتتاحي',
  'setup.inCol': 'وارد',
  'setup.outCol': 'صادر',
  'setup.expectedCol': 'الرصيد المتوقع',
  'setup.movementsCol': 'الحركات',
  'setup.noOpening': 'لا يوجد، يبدأ من 0',
  'setup.unassigned': 'غير مرتبط بحساب',
  'setup.unassignedHint': 'يتم تسجيل المدفوعات والمصروفات دون حساب، لذلك تدخل في إجمالي كل عملة فقط.',
  'setup.source.PAYMENTS': 'مدفوعات العملاء',
  'setup.source.EXPENSES': 'مصروفات مدفوعة',
  'setup.source.POINTS_CASH': 'مكافآت النقاط المدفوعة يدويًا',
  'setup.source.PARTNERS': 'أموال الشركاء',
  'setup.byCurrency': 'حسب العملة',
  'setup.partnersPreview': 'أرصدة الشركاء',
  'setup.issues': 'قبل الاعتماد',
  'setup.issuesNone': 'لا شيء يمنع الاعتماد.',
  'setup.blocking': 'يمنع الاعتماد',
  'setup.warning': 'يستحق المراجعة',
  'setup.issue.CUTOVER_IN_FUTURE': 'لم يحن يوم الانتقال بعد.',
  'setup.issue.LINE_IN_OLD_BASE': 'يوجد رصيد مقيّم بعملة {valuedIn}، والعملة الأساسية الآن {baseCurrency}. احفظه من جديد.',
  'setup.issue.LINE_CURRENCY_MISMATCH': 'رصيد {label} ليس بعملة حسابه.',
  'setup.issue.HISTORY_AFTER_CUTOVER': 'يمتد شهر تاريخي معتمد ({month}) إلى ما بعد تاريخ الانتقال، ولم تتم مراجعته مقابله. صحّحه مع المراجعة، أو غيّر تاريخ الانتقال.',
  'setup.issue.DRAFT_HISTORY_AFTER_CUTOVER': 'يمتد شهر تاريخي ما زال مسودة ({month}) إلى ما بعد تاريخ الانتقال، وسيحتاج إلى المراجعة.',
  'setup.issue.ACCOUNT_WITHOUT_OPENING': 'لا يوجد رصيد افتتاحي لحساب {label}، فيبدأ من 0.',
  'setup.issue.OPENING_ON_INACTIVE_ACCOUNT': 'الحساب {label} مغلق، لكن له رصيد افتتاحي.',
  'setup.issue.PARTNER_HISTORY_WITHOUT_OPENING': 'للشريك {label} أموال مسجّلة قبل تاريخ الانتقال، لكن ليس له مركز افتتاحي.',
  'setup.issue.PARTNER_EXPENSE_CANCELLED': 'دفع شريك المصروف «{label}»، ثم تم إلغاء المصروف، لكن رصيد الشريك ما زال يشمله.',
  'setup.issue.UNASSIGNED_MOVEMENTS': '{count} من المدفوعات والمصروفات منذ تاريخ الانتقال لا تحدد أي حساب، لذلك نحسبها حسب العملة.',
  'setup.issue.NEGATIVE_EXPECTED_BALANCE': 'الرصيد المتوقع في {label} أقل من الصفر ({expected}).',
  'setup.policy': 'سلف الشركاء',
  'setup.advanceLimit': 'الحد الأقصى لسلف كل شريك',
  'setup.advanceLimitHint': 'بعملة {base} وبأسعار اليوم. نرفض أي سلفة جديدة تتجاوزه. 0 يعني بلا حد.',
  'setup.saved': 'تم الحفظ.',
  'setup.error.CUTOVER_CONFIRMED': 'المركز الافتتاحي معتمد، فأعد فتحه أولًا.',
  'setup.error.CUTOVER_NOT_READY': 'يوجد ما يمنع الاعتماد. راجع «قبل الاعتماد».',
  'setup.error.CONCURRENT_CHANGE': 'غيّر شخص آخر المركز الافتتاحي في هذه الأثناء. راجعه من جديد.',
};
